/* =============================================================================
 * CoachAccountSettingsModal — a college coach's account settings (KGH-795)
 * -----------------------------------------------------------------------------
 * Name, title and the hide-name switch (PATCH /coach/me), the sign-in email,
 * on/off switches for the one notification a coach receives, and Sign out.
 * Nothing else: a coach has no messaging, so no Email Templates, and no phone
 * field.
 *
 * The switches write the same endpoints NotificationPreferencesModal uses. The
 * digest job reads coach_player_match on the email and push channels, and a
 * channel-level "off" silences it too, so "on" means both are on.
 * ========================================================================== */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, LogOut, X } from 'react-feather';
import Modal from './Modal';
import { Button, Field, Skeleton, TextInput, fieldAria } from './ui';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { BACKEND_CONFIG } from '../config/backendConfig';
import { coachAPI, isCoachApiError } from '../services/coachAPI';
import type { CoachMeResponse } from '../services/coachAPI.types';
import { font, radius, space } from '../styles/tokens';

interface CoachAccountSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Channel = 'email' | 'push';

/**
 * Fired on window after a details save here, with the saved coach as
 * `detail.coach`. A page that holds its own copy of those fields (the
 * recruiting profile page) listens so it does not show, and later re-save,
 * the old values.
 */
export const COACH_ME_UPDATED_EVENT = 'coach-me-updated';
export type CoachMeUpdatedDetail = { coach: CoachMeResponse['coach'] };

const MATCH_TYPE = 'coach_player_match';

const CHANNELS: Array<{ id: Channel; label: string }> = [
  { id: 'email', label: 'Email' },
  { id: 'push', label: 'Phone app' },
];

interface PrefsPayload {
  email_enabled?: boolean;
  push_enabled?: boolean;
  preferences?: Partial<Record<Channel | 'socket', Record<string, boolean>>>;
}

async function prefsRequest(token: string, path: string, body?: object): Promise<PrefsPayload> {
  const response = await fetch(`${BACKEND_CONFIG.baseURL}/notification-preferences${path}`, {
    method: body ? 'PUT' : 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : null),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) throw new Error(`notification-preferences ${response.status}`);
  const json = await response.json();
  return (json?.data || {}) as PrefsPayload;
}

function matchOn(prefs: PrefsPayload, channel: Channel): boolean {
  return prefs[`${channel}_enabled`] !== false && prefs.preferences?.[channel]?.[MATCH_TYPE] !== false;
}

function withMatch(prefs: PrefsPayload, channel: Channel, on: boolean, channelOn?: boolean): PrefsPayload {
  return {
    ...prefs,
    ...(channelOn === undefined ? null : { [`${channel}_enabled`]: channelOn }),
    preferences: {
      ...prefs.preferences,
      [channel]: { ...(prefs.preferences?.[channel] || {}), [MATCH_TYPE]: on },
    },
  };
}

/* ── pieces ─────────────────────────────────────────────────────────────── */

const SectionTitle: React.FC<{ id?: string; children: React.ReactNode }> = ({ id, children }) => (
  <h3
    id={id}
    style={{
      margin: `0 0 ${space[3]}px`,
      fontFamily: font.sans,
      fontSize: font.size.heading,
      fontWeight: font.weight.bold,
      color: 'var(--aps-ink)',
    }}
  >
    {children}
  </h3>
);

const Divider: React.FC = () => (
  <hr style={{ margin: `${space[5]}px 0`, border: 0, borderTop: '1px solid var(--aps-border)' }} />
);

/** A full-row switch. aria-disabled, not disabled, so focus stays put while it saves. */
const SwitchRow: React.FC<{
  label: string;
  checked: boolean;
  busy: boolean;
  onToggle: () => void;
}> = ({ label, checked, busy, onToggle }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-disabled={busy || undefined}
    aria-busy={busy || undefined}
    onClick={() => {
      if (!busy) onToggle();
    }}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: space[3],
      width: '100%',
      minHeight: 48,
      padding: `${space[2]}px ${space[3]}px`,
      background: 'var(--aps-surface)',
      border: '1px solid var(--aps-border)',
      borderRadius: radius.md,
      cursor: busy ? 'progress' : 'pointer',
      fontFamily: font.sans,
      fontSize: font.size.label,
      fontWeight: font.weight.semibold,
      color: 'var(--aps-ink)',
      textAlign: 'left',
    }}
  >
    <span>{label}</span>
    <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', gap: space[2], flexShrink: 0 }}>
      <span
        style={{
          minWidth: 24,
          fontSize: font.size.caption,
          fontWeight: font.weight.semibold,
          color: checked ? 'var(--aps-ink)' : 'var(--aps-text-muted)',
        }}
      >
        {checked ? 'On' : 'Off'}
      </span>
      <span
        style={{
          position: 'relative',
          width: 40,
          height: 24,
          borderRadius: radius.pill,
          border: '1px solid transparent',
          background: checked ? 'var(--aps-ink)' : 'var(--aps-border-strong)',
          opacity: busy ? 0.6 : 1,
          transition: 'background var(--aps-dur-fast)',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: checked ? 18 : 2,
            width: 18,
            height: 18,
            borderRadius: radius.pill,
            border: '1px solid transparent',
            background: 'var(--aps-surface)',
            boxShadow: 'var(--aps-elev-1)',
            transition: 'left var(--aps-dur-fast)',
          }}
        />
      </span>
    </span>
  </button>
);

/* ── the modal ──────────────────────────────────────────────────────────── */

const CoachAccountSettingsModal: React.FC<CoachAccountSettingsModalProps> = ({ isOpen, onClose }) => {
  const { user, sessionToken, logout, setUser } = useAuth();
  const toast = useToast();

  /* details */
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [hideName, setHideName] = useState(false);
  const [saved, setSaved] = useState({ name: '', title: '', hide: false });
  const [nameError, setNameError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savingDetails, setSavingDetails] = useState(false);

  /* notifications */
  const [prefs, setPrefs] = useState<PrefsPayload | null>(null);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [prefsError, setPrefsError] = useState(false);
  const [pending, setPending] = useState<Record<Channel, boolean>>({ email: false, push: false });

  /* sign out */
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const confirmSignOutRef = useRef<HTMLButtonElement>(null);

  const loadPrefs = useCallback(async () => {
    if (!sessionToken) return;
    setPrefsLoading(true);
    setPrefsError(false);
    try {
      setPrefs(await prefsRequest(sessionToken, ''));
    } catch {
      setPrefsError(true);
    } finally {
      setPrefsLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setNameError(null);
    setSaveError(null);
    setConfirmingSignOut(false);
    setEmail(user?.email || '');
    setDetailsLoading(true);
    coachAPI
      .me({ fresh: true })
      .then((res) => {
        if (cancelled) return;
        const n = res.coach?.name ?? '';
        const t = res.coach?.title ?? '';
        const h = res.coach?.hide_name_on_views === true;
        setName(n);
        setTitle(t);
        setHideName(h);
        setSaved({ name: n, title: t, hide: h });
        if (res.coach?.email) setEmail(res.coach.email);
      })
      .catch(() => {
        if (cancelled) return;
        // Fall back to what the session knows. Only changed fields are sent, so
        // an unknown title or switch is never cleared by a name edit.
        const n = user?.name || '';
        setName(n);
        setTitle('');
        setHideName(false);
        setSaved({ name: n, title: '', hide: false });
      })
      .finally(() => {
        if (!cancelled) setDetailsLoading(false);
      });
    loadPrefs();
    return () => {
      cancelled = true;
    };
    // user is read once per open; re-running on every context change would
    // wipe what the coach is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, loadPrefs]);

  useEffect(() => {
    if (confirmingSignOut) confirmSignOutRef.current?.focus();
  }, [confirmingSignOut]);

  const detailsDirty = name !== saved.name || title !== saved.title || hideName !== saved.hide;

  const saveDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    if (savingDetails || !detailsDirty) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('Enter your name.');
      return;
    }
    setNameError(null);
    setSaveError(null);
    setSavingDetails(true);
    try {
      const res = await coachAPI.updateMe({
        ...(name !== saved.name ? { name: trimmedName } : null),
        ...(title !== saved.title ? { title: title.trim() || null } : null),
        ...(hideName !== saved.hide ? { hide_name_on_views: hideName } : null),
      });
      const n = res.coach?.name ?? trimmedName;
      const t = res.coach?.title ?? '';
      const h = typeof res.coach?.hide_name_on_views === 'boolean' ? res.coach.hide_name_on_views : hideName;
      setName(n);
      setTitle(t);
      setHideName(h);
      setSaved({ name: n, title: t, hide: h });
      // The sidebar reads the name from the session.
      if (user && n !== user.name) setUser({ ...user, name: n });
      if (res.coach) {
        window.dispatchEvent(
          new CustomEvent<CoachMeUpdatedDetail>(COACH_ME_UPDATED_EVENT, { detail: { coach: res.coach } })
        );
      }
      toast.success('Your details are saved.');
      onClose();
    } catch (err) {
      setSaveError(isCoachApiError(err) ? err.message : 'That did not save. Try again.');
    } finally {
      setSavingDetails(false);
    }
  };

  const toggle = async (channel: Channel) => {
    if (!prefs || !sessionToken || pending[channel]) return;
    const wasOn = matchOn(prefs, channel);
    const next = !wasOn;
    const channelWasOn = prefs[`${channel}_enabled`] !== false;
    const typeWasOn = prefs.preferences?.[channel]?.[MATCH_TYPE] !== false;

    setPending((p) => ({ ...p, [channel]: true }));
    setPrefs((p) => (p ? withMatch(p, channel, next, next ? true : undefined) : p));
    try {
      if (next && !channelWasOn) {
        await prefsRequest(sessionToken, '/channel', { channel, enabled: true });
      }
      await prefsRequest(sessionToken, '/individual', { channel, type: MATCH_TYPE, enabled: next });
    } catch {
      setPrefs((p) => (p ? withMatch(p, channel, typeWasOn, channelWasOn) : p));
      toast.error('That did not save. Try again.');
    } finally {
      setPending((p) => ({ ...p, [channel]: false }));
    }
  };

  const signOut = async () => {
    setSigningOut(true);
    try {
      await logout();
    } catch {
      setSigningOut(false);
    }
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: font.sans,
    fontSize: font.size.labelSm,
    color: 'var(--aps-text-2)',
    lineHeight: 1.5,
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      labelledById="coach-account-title"
      isDirty={detailsDirty && !savingDetails}
      disableClose={savingDetails || signingOut}
      discardTitle="Discard your changes?"
      discardMessage="Your changes are not saved."
      contentStyle={{ maxWidth: 480, background: 'var(--aps-surface)' }}
    >
      <div style={{ padding: space[5], fontFamily: font.sans }}>
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: space[3], marginBottom: space[5] }}>
          <h2
            id="coach-account-title"
            style={{
              flex: 1,
              margin: 0,
              fontSize: font.size.heading + 2,
              fontWeight: font.weight.heavy,
              color: 'var(--aps-ink)',
            }}
          >
            Account settings
          </h2>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="Close"
            onClick={onClose}
            disabled={savingDetails || signingOut}
            style={{ minWidth: 44, minHeight: 44, justifyContent: 'center' }}
          >
            <X size={18} aria-hidden="true" />
          </Button>
        </div>

        {/* your details */}
        <form onSubmit={saveDetails} noValidate>
          <SectionTitle>Your details</SectionTitle>
          <p style={{ ...labelStyle, margin: `-${space[2]}px 0 ${space[4]}px` }}>
            Families see this when you view a player&apos;s profile, unless you hide it.
          </p>
          {detailsLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: space[3] }}>
              <Skeleton variant="rect" height={64} aria-label="Loading your details" />
              <Skeleton variant="rect" height={64} />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: space[4] }}>
              <Field id="coach-account-name" label="Your name" required error={nameError}>
                <TextInput
                  {...fieldAria('coach-account-name', { error: nameError })}
                  type="text"
                  value={name}
                  maxLength={120}
                  invalid={!!nameError}
                  autoComplete="name"
                  onChange={(e) => {
                    setName(e.target.value);
                    if (nameError) setNameError(null);
                    if (saveError) setSaveError(null);
                  }}
                  disabled={savingDetails}
                />
              </Field>
              <Field id="coach-account-title-input" label="Your title">
                <TextInput
                  id="coach-account-title-input"
                  type="text"
                  value={title}
                  maxLength={100}
                  placeholder="Assistant Coach"
                  autoComplete="organization-title"
                  onChange={(e) => {
                    setTitle(e.target.value);
                    if (saveError) setSaveError(null);
                  }}
                  disabled={savingDetails}
                />
              </Field>
              <div>
                <SwitchRow
                  label="Hide my name when I view a profile"
                  checked={hideName}
                  busy={savingDetails}
                  onToggle={() => {
                    setHideName((v) => !v);
                    if (saveError) setSaveError(null);
                  }}
                />
                <p style={{ ...labelStyle, margin: `${space[1]}px 0 0` }}>Families still see your school.</p>
              </div>
              {saveError && (
                <p
                  role="alert"
                  style={{
                    display: 'flex',
                    gap: space[2],
                    margin: 0,
                    fontSize: font.size.labelSm,
                    fontWeight: font.weight.semibold,
                    color: 'var(--aps-danger)',
                    lineHeight: 1.5,
                  }}
                >
                  <AlertCircle size={16} aria-hidden style={{ flexShrink: 0, marginTop: 1, color: 'var(--aps-danger)' }} />
                  {saveError}
                </p>
              )}
              <div>
                <Button type="submit" size="md" loading={savingDetails} disabled={!detailsDirty || savingDetails}>
                  {savingDetails ? 'Saving' : 'Save'}
                </Button>
              </div>
            </div>
          )}
        </form>

        <Divider />

        {/* sign-in email */}
        <SectionTitle>Sign-in email</SectionTitle>
        <p
          style={{
            margin: 0,
            fontSize: font.size.label,
            fontWeight: font.weight.semibold,
            color: 'var(--aps-ink)',
            overflowWrap: 'anywhere',
          }}
        >
          {email || 'No email on file'}
        </p>

        <Divider />

        {/* notifications */}
        <SectionTitle id="coach-account-notif-title">Notifications</SectionTitle>
        <div role="group" aria-labelledby="coach-account-notif-title coach-account-notif-type">
          <p
            id="coach-account-notif-type"
            style={{ margin: 0, fontSize: font.size.label, fontWeight: font.weight.semibold, color: 'var(--aps-ink)' }}
          >
            New player matches
          </p>
          <p style={{ ...labelStyle, margin: `2px 0 ${space[3]}px` }}>
            When new players match your recruiting profile.
          </p>

          {prefsLoading && !prefs && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: space[2] }}>
              <Skeleton variant="rect" height={48} aria-label="Loading your notifications" />
              <Skeleton variant="rect" height={48} />
            </div>
          )}

          {!prefsLoading && prefsError && !prefs && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: space[2] }}>
              <span style={labelStyle}>Your notifications did not load.</span>
              <Button type="button" variant="secondary" size="sm" onClick={loadPrefs}>
                Try again
              </Button>
            </div>
          )}

          {prefs && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: space[2] }}>
              {CHANNELS.map((c) => (
                <SwitchRow
                  key={c.id}
                  label={c.label}
                  checked={matchOn(prefs, c.id)}
                  busy={pending[c.id]}
                  onToggle={() => toggle(c.id)}
                />
              ))}
            </div>
          )}
        </div>

        <Divider />

        {/* sign out */}
        {!confirmingSignOut ? (
          <Button
            type="button"
            variant="secondary"
            size="md"
            leftIcon={<LogOut size={16} aria-hidden />}
            onClick={() => setConfirmingSignOut(true)}
            disabled={savingDetails}
            style={{ width: '100%', justifyContent: 'center', minHeight: 44 }}
          >
            Sign out
          </Button>
        ) : (
          <div
            style={{
              padding: space[3],
              border: '1px solid var(--aps-border)',
              borderRadius: radius.lg,
              background: 'var(--aps-surface-2)',
            }}
          >
            <p style={{ margin: `0 0 ${space[3]}px`, fontSize: font.size.label, color: 'var(--aps-ink)' }}>
              Sign out of APS Recruits?
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: space[2] }}>
              <Button
                ref={confirmSignOutRef}
                type="button"
                variant="danger"
                size="md"
                loading={signingOut}
                leftIcon={<LogOut size={16} aria-hidden />}
                onClick={signOut}
                style={{ flex: '1 1 140px', justifyContent: 'center', minHeight: 44 }}
              >
                Sign out
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="md"
                disabled={signingOut}
                onClick={() => setConfirmingSignOut(false)}
                style={{ flex: '1 1 120px', justifyContent: 'center', minHeight: 44 }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CoachAccountSettingsModal;
